import {
  useState,
  ChangeEvent,
  FormEvent,
  useEffect,
  memo,
  useCallback,
} from "react";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { useAuth } from "../../hooks";
import { ErrorDisplay } from "../common/ErrorDisplay";

/**
 * Login form styled to the "Login Split v2" mock: underline inputs on the
 * cream panel, olive submit button. Sits inside LoginPage's right panel,
 * which provides the "Welcome back" heading.
 */

const inputClasses =
  "h-11 w-full border-0 border-b-[1.5px] border-[#D5D0C0] bg-transparent px-0.5 text-[15px] text-[#1A1B12] caret-[#5C6638] outline-none transition-colors placeholder:text-[#A8A3A0] focus:border-[#5C6638] disabled:opacity-60";

const labelClasses = "mb-2 block text-xs font-medium text-[#84806F]";

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const {
    login,
    isLoading,
    error: authError,
    requiresMfa,
    clearError,
  } = useAuth();

  // Dismiss a stale auth error once the user edits a field. This must hang off
  // the change handlers, not an effect watching authError — an effect fires
  // when the error is first set and would clear it before it's ever seen.
  const handleFieldChange = useCallback(
    (setter: (value: string) => void) => (e: ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value);
      if (authError) {
        clearError();
      }
    },
    [authError, clearError],
  );

  // Handle MFA requirement changes
  useEffect(() => {
    if (requiresMfa) {
      const from = (location.state as any)?.from || { pathname: "/" };
      navigate("/auth/mfa", {
        state: { from },
        replace: true,
      });
    }
  }, [requiresMfa, navigate, location.state]);

  // Validate credentials
  const validateCredentials = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!email) {
      errors.email = "Email is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      errors.email = "Invalid email address";
    }

    if (!password) {
      errors.password = "Password is required";
    } else if (password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [email, password]);

  // Memoized form submission handler
  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      // Reset validation errors
      setValidationErrors({});

      // Validate credentials
      if (!validateCredentials()) {
        return;
      }

      try {
        await login({ email, password });

        // Don't navigate here - let the parent component handle navigation
        // based on the full authentication state including forcePasswordChange
      } catch (error) {
        // Error is handled in AuthProvider and available via authError
      }
    },
    [login, email, password, validateCredentials],
  );

  // Memoized password visibility toggle
  const toggleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  return (
    <form onSubmit={handleSubmit} className="w-full">
      {/* Display authentication errors using ErrorDisplay */}
      {authError && <ErrorDisplay error={authError} onClose={clearError} />}

      <div className="mb-[26px]">
        <label htmlFor="email" className={labelClasses}>
          Email address
        </label>
        <input
          id="email"
          name="email"
          type="text"
          autoComplete="email"
          autoFocus
          placeholder="you@company.com"
          value={email}
          onChange={handleFieldChange(setEmail)}
          disabled={isLoading}
          aria-invalid={!!validationErrors.email}
          className={inputClasses}
        />
        {validationErrors.email && (
          <p className="mt-1.5 text-xs text-red-600">
            {validationErrors.email}
          </p>
        )}
      </div>

      <div className="mb-[34px]">
        <label htmlFor="password" className={labelClasses}>
          Password
        </label>
        <div className="relative">
          <input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            placeholder="••••••••••"
            value={password}
            onChange={handleFieldChange(setPassword)}
            disabled={isLoading}
            aria-invalid={!!validationErrors.password}
            className={`${inputClasses} pr-[30px]`}
          />
          <button
            type="button"
            onClick={toggleShowPassword}
            disabled={isLoading}
            aria-label="toggle password visibility"
            className="absolute right-0.5 top-[13px] text-[#A8A3A0] hover:text-[#84806F] disabled:opacity-60"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {showPassword ? (
                <>
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </>
              ) : (
                <>
                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                  <circle cx="12" cy="12" r="3" />
                </>
              )}
            </svg>
          </button>
        </div>
        {validationErrors.password && (
          <p className="mt-1.5 text-xs text-red-600">
            {validationErrors.password}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={isLoading || !email || !password}
        className="inline-flex h-12 w-full items-center justify-center rounded-[10px] bg-[#5C6638] text-[14.5px] font-semibold text-[#F4F2E8] transition-colors hover:bg-[#4A522D] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isLoading ? (
          <CircularProgress size={20} sx={{ color: "#F4F2E8" }} />
        ) : (
          "Sign in"
        )}
      </button>

      <div className="mt-5 text-center">
        <RouterLink
          to="/auth/reset-password-request"
          className="text-[13px] font-medium text-[#4A522D] hover:underline"
        >
          Forgot your password?
        </RouterLink>
      </div>
    </form>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default memo(LoginForm);
